'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { useLocale } from 'next-intl';
import { FormHTMLAttributes } from 'react';
import { useForm } from 'react-hook-form';
import { CgSpinner } from 'react-icons/cg';
import { z } from 'zod';

import { Agreements } from '../../[id]/components/atoms/Agreements';
import { getContactEmail } from '../lib/contact';

import Input from './Input';

import { Checkbox } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { Link } from '@lib/navigation';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { IPRESSO_SOURCES } from '@utils/api/client/ipresso';
import { IpressoFetchResponse, IpressoPostBody } from '@utils/api/server/ipresso';
import { getIPressoAgreements } from '@utils/ipresso';

interface PropTypes extends FormHTMLAttributes<HTMLFormElement> {
  labels: {
    firstName: string;
    lastName: string;
    email: string;
    message: string;
    submit: string;
    agreement: {
      checkAll: string;
      marketing: {
        label: string;
        contents: string;
      };
      commercialInside: {
        label: string;
        contents: string;
      };
      commercialOutside: {
        label: string;
        contents: string;
      };
    };
    rodo: {
      title: string;
      content: string;
    };
    toast: {
      success: string;
      error: string;
    };
  };
}
const schema = z.object({
  email: z.string().email(),
  firstName: z.string().min(2).max(255),
  lastName: z.string().min(2).max(255),
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialInside: z.boolean().optional(),
  commercialOutside: z.boolean().optional(),
  message: z.string().min(9),
});

const schemaDE = z.object({
  email: z.string().email(),
  firstName: z.string().min(2).max(255),
  lastName: z.string().min(2).max(255),
  agreementDE: z.boolean().refine((value) => value === true),
  message: z.string().min(9),
});

const isDESchema = (
  _schema: z.infer<typeof schema> | z.infer<typeof schemaDE>,
): _schema is z.infer<typeof schemaDE> => {
  return 'agreementDE' in _schema;
};

export default function Form(props: PropTypes) {
  const locale = useLocale();
  const { labels, ...rest } = props;

  const usedSchema = locale === 'de' ? schemaDE : schema;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<z.infer<typeof usedSchema>>({
    resolver: zodResolver(usedSchema),
  });

  const contactSubmit = handleSubmit(async (data) => {
    const agreements = getIPressoAgreements(
      isDESchema(data)
        ? { marketingAgreement: data.agreementDE }
        : {
          marketingAgreement: data.marketingAgreement,
          commercialInside: data.commercialInside,
          commercialOutside: data.commercialOutside,
        },
    );

    try {
      const bodyObject: IpressoPostBody = {
        contactData: { email: data.email },
        lang: {
          agreement: locale,
        },
        agreementData: agreements,
        source: IPRESSO_SOURCES.CONTACT,
      };

      await fetchApi<IpressoFetchResponse>('/campaign/edit', {
        method: 'POST',
        noAuth: true,
        body: JSON.stringify(bodyObject),
      });

      await fetchApi('/contact', {
        method: 'POST',
        noAuth: true,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient: getContactEmail(locale),
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          consent: isDESchema(data) ? data.agreementDE : data.marketingAgreement,
          message: data.message,
          locale,
          source: 'contact',
        }),
      }).then(() => {
        toastSuccess(labels.toast.success);
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      logger.error(e, 'Contact page %s', (e as any)?.message);
      toastError(labels.toast.error);
    }
  });

  return (
    <form onSubmit={contactSubmit} {...rest}>
      <div className="mb-3">
        <Input className="mb-1" label={labels.firstName} error={!!errors.firstName} {...register('firstName')} />
        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName.message}</p>}
      </div>
      <div className="mb-3">
        <Input label={labels.lastName} className="mb-1" error={!!errors.lastName} {...register('lastName')} />
        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName.message}</p>}
      </div>
      <div className="mb-3">
        <Input className="mb-1" type="email" label={labels.email} error={!!errors.email} {...register('email')} />
        {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
      </div>
      <div className="relative mb-3">
        <label className="absolute -top-2 left-3 bg-white px-1 text-sm text-xs text-premium-dark dark:bg-premium-darker dark:text-white">
          {labels.message}
        </label>
        <textarea
          className="mb-1 h-32 w-full rounded-md border-2 border-premium-dark border-opacity-60 px-4 py-2 placeholder:text-gray-500 dark:border-gray-650 dark:bg-premium-darker dark:text-white"
          {...register('message')}
        />
        {/* <Input
          className="mb-1"
          placeholder={labels.message}
          error={!!errors.message}
          {...register('message')}
        /> */}
        {errors.message && <p className="text-xs text-red-500">{errors.message.message}</p>}
      </div>

      {locale !== 'de' ? (
        <>
          <Agreements commercialInsideRequired={true} setValue={setValue} watch={watch} register={register} errors={errors} />
        </>
      ) : (
        <Checkbox
          label={
            <span>
              Ich habe die&nbsp;
              <Link className="text-premium-500 underline" href="/rules">
                Nutzungsbedingungen
              </Link>
              &nbsp;gelesen und zur Kenntnis genommen
            </span>
          }
          wrapperClassName="mb-2 lg:mb-3"
          {...register('agreementDE')}
        />
      )}
      <div className="text-center lg:text-left">
        <button
          disabled={isSubmitting}
          className={classNames(
            'my-4 inline-flex items-center justify-center  rounded-full bg-premium-dark px-16 py-3 font-bold text-white transition-colors hover:bg-premium-700 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-premium-900 lg:my-8 lg:w-[unset] dark:bg-white dark:text-premium-900 dark:hover:bg-premium-900 dark:hover:text-white dark:disabled:hover:bg-white dark:disabled:hover:text-premium-900',
          )}
        >
          {isSubmitting && <CgSpinner className="mr-1 animate-spin" />}
          {labels.submit}
        </button>
      </div>
    </form>
  );
}
